import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { authApi } from '../api/auth';
import { joinCache } from '../utils/joinCache';
import type { ActorResponse } from '../api/types';

export type Actor = ActorResponse['actor'];

type AuthState = {
  actor: Actor;
  loading: boolean;
  refresh: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (
    email: string,
    password: string,
    displayName: string,
    mergeGuestId: boolean,
  ) => Promise<void>;
  signOut: () => Promise<void>;
};

const AuthCtx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [actor, setActor] = useState<Actor>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await authApi.me();
      setActor(res.actor);
    } catch {
      setActor(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      await authApi.signin(email, password);
      await refresh();
    },
    [refresh],
  );

  const signUp = useCallback(
    async (email: string, password: string, displayName: string, mergeGuestId: boolean) => {
      await authApi.signup(email, password, displayName, mergeGuestId);
      await refresh();
    },
    [refresh],
  );

  const signOut = useCallback(async () => {
    await authApi.signout();
    joinCache.clearAll();
    await refresh();
  }, [refresh]);

  const value = useMemo<AuthState>(
    () => ({ actor, loading, refresh, signIn, signUp, signOut }),
    [actor, loading, refresh, signIn, signUp, signOut],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
